/**
 * The runtime's `_results.<name>` write surface, projected from
 * `ontology/runtime-result-writes.v1.json` (derived by
 * scripts/build-runtime-result-writes.ts from the @cogo/runtime-v2 sources).
 *
 * `actionflow-result-not-defined` decides whether a `#{_results.X}` read has
 * a producer. It used to carry that knowledge as a hand-written constant that
 * modelled two of the runtime's four write paths and had drifted on one of
 * those two. cogo-validator cannot import @cogo/runtime-v2, but it can read a
 * projection of it that regenerates and fails a staleness gate.
 */
import runtimeResultWrites from "../../ontology/runtime-result-writes.v1.json" with { type: "json" }

/**
 * Actions whose handler can return a value, so the engine's
 * `if (result !== undefined && step.name) setState('_results.'+step.name)`
 * fires for them. Derived from each handler's declared return type.
 */
export const AUTO_CAPTURE_ACTIONS: ReadonlySet<string> = new Set(
  runtimeResultWrites.autoCaptureActions,
)

/**
 * Executors that fall back to a fixed `_results.<slot>` when the step gives
 * no target param — `pickFiles` writes `_results.pickFiles`, `graphqlAction`
 * writes `_results.graphql`. A step using one of these defines that slot even
 * though the flow never names it.
 */
export const DEFAULT_RESULT_SLOTS: Readonly<Record<string, string>> =
  runtimeResultWrites.defaultSlots

/** Slots written on a fixed path, independent of any param. */
export const UNCONDITIONAL_RESULT_SLOTS: readonly string[] =
  runtimeResultWrites.unconditionalSlots

/**
 * Param names an executor reads as "where to put the result", derived from
 * the engine's `read(<param>, "target")` and `stateTarget(<param>, ...)`
 * declaration sites (was a hand-written three-entry array here).
 *
 * These are the spellings a step may use to NAME its own slot. The rule used
 * them for exactly one decision — "the step named a target, so do not add the
 * executor's default slot" — and then threw the name away, so a `fileUpload`
 * with `resultPath: "_results.avatarFile"` suppressed `_results.file` and
 * defined nothing in its place. `saas/account-settings` took 3
 * `actionflow-result-not-defined` errors for a slot the runtime does write.
 */
export const RESULT_TARGET_PARAMS: readonly string[] =
  runtimeResultWrites.explicitTargetParams

/**
 * The `_results.<name>` slot a step names for itself, or `null`.
 *
 * Only a value that actually addresses `_results.` counts: `setState`'s
 * `target: "avatarUrl"` is a STATE path, and admitting it would let
 * `#{_results.avatarUrl}` — which the runtime does not write — pass. The
 * engine writes `_results.${step.name}` on auto-capture, never `params.target`,
 * and that distinction is the whole point of this rule.
 */
export function namedResultSlot(
  params: Record<string, unknown> | undefined,
): string | null {
  for (const key of RESULT_TARGET_PARAMS) {
    const raw = params?.[key]
    if (typeof raw !== "string") continue
    const path = raw.replace(/^#/, "").replace(/^\{|\}$/g, "")
    if (path.startsWith("_results.")) return path
  }
  return null
}
